import { useState } from "react";
import { BsPin, BsPinFill } from "react-icons/bs";
import { FiAlertTriangle, FiArrowUpRight, FiCalendar } from "react-icons/fi";

import { renderTextWithLinks } from "@/app/[tenant]/(console)/comms/chat/window/message/shared";

import QuickNoteDialog from "@/components/custom-calendar/quick-note-dialog";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import { ProgressBarLink } from "@/components/progress-bar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownSettingsButton,
} from "@/components/ui/dropdown";
import { ExpandableText } from "@/components/ui/expandable-text";

import { formatDateTime, formatStaffName } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";

import { UserType } from "@/types/enums";
import { AttendeeDetail, EventDetail, EventTiny } from "@/types/event";
import { QuickNoteSummary } from "@/types/quick-note";
import { UserTiny } from "@/types/user";

import {
  useDeleteQuickNoteMutation,
  useTogglePinQuickNoteMutation,
} from "@/api/provider/user/patient/quick-note-mutation";
import { useMemoWindowStore } from "@/stores/memo-window-store";

type QuickNoteCardSize = "sm" | "md" | "lg";

interface QuickNoteCardProps {
  note: QuickNoteSummary;
  size?: QuickNoteCardSize;
  onNoteUpdate?: (note: QuickNoteSummary) => void;
  onNoteDelete?: (noteId: string) => void;
  event?: EventTiny | EventDetail;
  patient?: UserTiny;
  /**
   * Whether the Pin/Unpin action is available. Pinning is only supported for
   * patient memos, not appointment memos. Defaults to `!note.eventId`, which
   * suppresses Pin for any saved appointment memo automatically. Appointment
   * contexts that render *unsaved* drafts (no eventId yet) must still pass
   * `allowPin={false}` explicitly — e.g. the appointment-create form.
   */
  allowPin?: boolean;
  /**
   * When true, edit/delete stay in memory (via the callbacks) instead of
   * hitting the API — for unsaved draft memos in the appointment-create form.
   */
  deferPersistence?: boolean;
  /** Prevent memo actions while the parent form is being submitted. */
  disabled?: boolean;
  /**
   * Label memos written on an appointment with its date, linking to it. Off by
   * default because most callers already show the memo inside its appointment.
   */
  showAppointmentTag?: boolean;
}

const sizeClasses = {
  sm: {
    container: "gap-2 p-3",
    avatar: "size-6 text-tiny",
    clampLines: 4,
  },
  md: {
    container: "gap-2 p-3",
    avatar: "size-7 text-caption-strong",
    clampLines: 5,
  },
  lg: {
    container: "gap-3 p-4",
    avatar: "size-8 text-caption-strong",
    clampLines: 6,
  },
} as const;

export default function QuickNoteCard({
  note,
  size = "md",
  onNoteUpdate,
  onNoteDelete,
  event,
  patient,
  allowPin = !note.eventId,
  deferPersistence = false,
  disabled = false,
  showAppointmentTag = false,
}: QuickNoteCardProps) {
  const { toast } = useToast();
  const [isEditNoteOpen, setIsEditNoteOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const deleteQuickNoteMutation = useDeleteQuickNoteMutation();
  const togglePinMutation = useTogglePinQuickNoteMutation();
  const openMemoWindow = useMemoWindowStore((s) => s.openMemoWindow);

  const classes = sizeClasses[size];

  const resolvePatientId = () =>
    patient?.id ||
    event?.attendees?.find(
      (a: AttendeeDetail) => a.user?.type === UserType.PATIENT,
    )?.user?.id;

  // A saved memo with no caller to notify is edited in the console-wide memo
  // window, which survives navigating away while it is open. Drafts and
  // callers that track the edit (onNoteUpdate) keep the local window below,
  // which stays modal: the card can unmount and would take the draft with it.
  const usesSharedWindow = !deferPersistence && !onNoteUpdate;

  const handleEdit = () => {
    const resolvedPatient =
      patient ||
      event?.attendees?.find(
        (a: AttendeeDetail) => a.user?.type === UserType.PATIENT,
      )?.user;
    if (usesSharedWindow && resolvedPatient) {
      openMemoWindow({ patient: resolvedPatient, event, existingNote: note });
    } else {
      setIsEditNoteOpen(true);
    }
  };

  const handleTogglePin = () => {
    const patientId = resolvePatientId();
    if (!patientId) {
      toast({
        title: "Error",
        description: "Failed to pin note, no patient found",
        variant: "error",
      });
      return;
    }
    togglePinMutation.mutate({ noteId: note.id, patientId });
  };

  const handleDeleteNote = async () => {
    if (deferPersistence) {
      if (!onNoteDelete) return;
      onNoteDelete(note.id);
      setIsDeleteConfirmOpen(false);
      return;
    }
    const patientId = resolvePatientId();
    if (!patientId) {
      toast({
        title: "Error",
        description: "Failed to delete note, no patient found",
        variant: "error",
      });
      return;
    }
    const success = await deleteQuickNoteMutation.mutateAsync({
      noteId: note.id,
      patientId,
    });
    if (success) {
      onNoteDelete?.(note.id);
      toast({
        title: "Success",
        description: "Note deleted successfully.",
        variant: "success",
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to delete note",
        variant: "error",
      });
    }
    setIsDeleteConfirmOpen(false);
  };

  return (
    <>
      <article
        className={cx(
          "flex flex-col rounded-lg border bg-background transition-colors",
          note.isAlert
            ? "border-status-error-text/40 bg-status-error-bg/40"
            : note.isPinned && "border-primary/40",
          classes.container,
        )}
      >
        <header className="flex items-start gap-2">
          <div
            aria-hidden
            className={cx(
              "flex shrink-0 items-center justify-center rounded-full bg-background-tertiary uppercase text-foreground-secondary",
              classes.avatar,
            )}
          >
            {note.creator?.firstName?.[0]}
            {note.creator?.lastName?.[0]}
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-body-strong text-foreground">
              {formatStaffName(note.creator)}
            </p>
            <p className="truncate text-caption text-foreground-tertiary">
              {formatDateTime(note.createdDate)}
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <DropdownSettingsButton
                className="-mr-1 -mt-1 shrink-0"
                disabled={disabled}
                aria-label="Memo actions"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32">
              {allowPin && (
                <DropdownMenuItem
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleTogglePin();
                  }}
                >
                  {note.isPinned ? (
                    <BsPinFill className="mr-2 h-3.5 w-3.5 text-primary" />
                  ) : (
                    <BsPin className="mr-2 h-3.5 w-3.5" />
                  )}
                  <span>{note.isPinned ? "Unpin" : "Pin"}</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleEdit();
                }}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-status-error-text"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setIsDeleteConfirmOpen(true);
                }}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {(note.isAlert ||
          note.isPinned ||
          (showAppointmentTag && note.eventId)) && (
          <div className="flex flex-wrap items-center gap-1.5">
            {note.isAlert && (
              <span className="inline-flex items-center gap-1 rounded bg-status-error-bg px-1.5 py-0.5 text-caption-strong text-status-error-text">
                <FiAlertTriangle className="size-3" aria-hidden />
                Alert
              </span>
            )}
            {note.isPinned && (
              <span className="inline-flex items-center gap-1 rounded bg-background-secondary px-1.5 py-0.5 text-caption-strong text-primary">
                <BsPinFill className="size-3" aria-hidden />
                Pinned
              </span>
            )}
            {showAppointmentTag && note.eventId && (
              <ProgressBarLink
                href={`/check-in/appointment-form/${note.eventId}`}
                title="Open this appointment"
                className="group inline-flex items-center gap-1 rounded bg-background-secondary px-1.5 py-0.5 text-caption-strong text-foreground-secondary transition-colors hover:bg-hover hover:text-foreground"
              >
                <FiCalendar className="size-3" aria-hidden />
                <span>
                  {note.eventStart
                    ? `Appointment · ${formatDateTime(note.eventStart, true, false)}`
                    : "Appointment"}
                </span>
                <FiArrowUpRight
                  className="size-3 opacity-60 transition-opacity group-hover:opacity-100"
                  aria-hidden
                />
              </ProgressBarLink>
            )}
          </div>
        )}

        <ExpandableText
          text={note.note}
          clampLines={classes.clampLines}
          textClassName="text-body text-foreground"
          renderText={renderTextWithLinks}
        />
      </article>

      <QuickNoteDialog
        event={event}
        patient={patient}
        existingNote={note}
        deferPersistence={deferPersistence}
        open={isEditNoteOpen}
        onOpenChange={setIsEditNoteOpen}
        onNoteUpdate={
          onNoteUpdate
            ? (updatedNote: QuickNoteSummary) => {
                onNoteUpdate(updatedNote);
                setIsEditNoteOpen(false);
              }
            : undefined
        }
      />

      <DeleteConfirmationModal
        isOpen={isDeleteConfirmOpen}
        setIsOpen={setIsDeleteConfirmOpen}
        onConfirm={handleDeleteNote}
        title="Delete Note"
        subtitle="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete"
      />
    </>
  );
}
